import { type TutorialLevel } from "./tutorial-data";

const TEST_TUTORIAL_2: TutorialLevel = {
  name: "Basic Logic",
  description:
    "Learn some of the logic techniques that you'll need to solve most puzzles.",
  gridLayout: [
    [0, 0, 1, 1, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 1, 1],
    [1, 0, 1, 1, 0],
    [1, 1, 1, 0, 0],
  ],
  phases: [
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          Sometimes there <strong>isn't enough information</strong> for you to
          fill out a whole row or column at once.
        </p>
      ),
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: <p>Let's take a look at this column.</p>,
      vignettes: [
        {
          type: "column",
          index: 0,
        },
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          The clue tells us that four consecutive squares are colored in.
          However,{" "}
          <strong>
            there's no way to know exactly which squares it's referring to
          </strong>
          .
        </p>
      ),
      vignettes: [
        {
          type: "columnClue",
          index: 0,
        },
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          However, if we use logic{" "}
          <strong>it's still possible to color in some squares</strong>. Let's{" "}
          take a look.
        </p>
      ),
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          There are <strong>only two arrangements</strong> that four consecutive
          squares can be placed in...
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 0,
        },
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: <p>This one...</p>,
      vignettes: [
        {
          type: "column",
          index: 0,
        },
      ],
      demoFadeTiles: [
        [
          [0, 0],
          [0, 1],
          [0, 2],
          [0, 3],
        ],
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: <p>... and this one...</p>,
      vignettes: [
        {
          type: "column",
          index: 0,
        },
      ],
      demoFadeTiles: [
        [
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4],
        ],
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          Looking at both arrangements, you'll see that{" "}
          <strong>the three squares in the middle are always colored in</strong>
          .
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 0,
        },
      ],
      highlightRects: [{ start: [0, 1], end: [0, 3] }],
      demoFadeTiles: [
        [
          [0, 0],
          [0, 1],
          [0, 2],
          [0, 3],
        ],
        [
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4],
        ],
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          Therefore, <strong>we can safely color in these squares</strong> even
          if we don't know where to put the fourth one yet.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 0,
        },
      ],
      highlightRects: [{ start: [0, 1], end: [0, 3] }],
      demoFadeTiles: [
        [
          [0, 0],
          [0, 1],
          [0, 2],
          [0, 3],
        ],
        [
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4],
        ],
      ],
    },
    {
      waitFor: "tileFillExact",
      allowGridInput: true,
      message: (
        <p>
          <strong>Color those squares in now.</strong>
        </p>
      ),
      requireColor: [
        [0, 1],
        [0, 2],
        [0, 3],
      ],
      highlightRects: [{ start: [0, 1], end: [0, 3] }],
      vignettes: [
        {
          type: "column",
          index: 0,
        },
      ],
    },
    {
      waitFor: "tileFillExact",
      allowGridInput: true,
      message: (
        <p>
          You can use the same principle on this column.{" "}
          <strong>See if you can do it on your own.</strong>
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 4,
        },
      ],
      requireColor: [[4, 2]],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <>
          <p>
            <strong>Great!</strong>
          </p>
          <p>
            You can use the same logic on columns with{" "}
            <strong>multiple clues</strong>. Let's look at this one.
          </p>
        </>
      ),
      vignettes: [
        {
          type: "columnClue",
          index: 2,
        },
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <>
          <p>
            For this column, there are three possible configurations of
            colored-in squares, but{" "}
            <strong>one square is present in all of them.</strong>
          </p>
          <p>Do you see it?</p>
        </>
      ),
      vignettes: [
        {
          type: "column",
          index: 2,
        },
      ],
      demoFadeTiles: [
        [
          [2, 0],
          [2, 2],
          [2, 3],
        ],
        [
          [2, 0],
          [2, 3],
          [2, 4],
        ],
        [
          [2, 1],
          [2, 3],
          [2, 4],
        ],
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          It's <strong>this one</strong> right here.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 2,
        },
      ],
      highlightRects: [{ start: [2, 3], end: [2, 3] }],
      demoFadeTiles: [
        [
          [2, 0],
          [2, 2],
          [2, 3],
        ],
        [
          [2, 0],
          [2, 3],
          [2, 4],
        ],
        [
          [2, 1],
          [2, 3],
          [2, 4],
        ],
      ],
    },
    {
      waitFor: "tileFillExact",
      allowGridInput: true,
      message: (
        <p>
          <strong>Fill it in now</strong>.
        </p>
      ),
      requireColor: [[2, 3]],
      highlightRects: [{ start: [2, 3], end: [2, 3] }],
      vignettes: [
        {
          type: "column",
          index: 2,
        },
      ],
    },
    {
      waitFor: "puzzleComplete",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <>
          <p>
            Good! Now use the skills you learned in this lesson to{" "}
            <strong>finish the puzzle</strong>.
          </p>
          <p>
            If you get stuck, don't forget to{" "}
            <strong>X-out squares that cannot possibly be colored-in</strong>.
          </p>
        </>
      ),
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          <strong>Great job!</strong>
        </p>
      ),
    },
  ],
};

export default TEST_TUTORIAL_2;
